import { detectPlatform } from "../../platform.js";
import { useTasksStore } from "../../stores/index.js";
import { expandTasksWindowed } from "../calendar-view/build-days.js";
import { capacitorDiagnostics, MAX_SCHEDULED } from "./adapters/capacitor.js";
import { reconcile, type ScheduledFire } from "./NotificationScheduler.js";

const WINDOW_MIN = detectPlatform() === "capacitor" ? 60 * 24 * 14 : 60 * 24;

/**
 * Run the exact reminder pipeline the reconciler uses and return a human-readable
 * report — so the user can see, on the phone, whether the app produces any
 * reminders to schedule (a data question) and whether the OS actually has them
 * scheduled (a delivery question), without a desktop + Chrome DevTools.
 */
export async function diagnoseReminders(): Promise<string> {
  const { tasks, rules, overrides } = useTasksStore.getState();
  const now = new Date();
  // Match the reconciler: expand from start-of-today (intersectsWindow truncates
  // due to start-of-day, so `now` would drop tasks due later today).
  const from = new Date(now);
  from.setHours(0, 0, 0, 0);
  const to = new Date(now.getTime() + WINDOW_MIN * 60_000);
  const expanded = expandTasksWindowed(tasks, rules, overrides, from, to);

  const live = tasks.filter((t) => !t.deleted_at);
  const events = live.filter((t) => t.is_event);
  const withReminder = live.filter((t) => t.reminder_offsets.length > 0);

  // Capture what reconcile WOULD hand the OS, using a fake sink (no side effects).
  let fires: ScheduledFire[] = [];
  await reconcile(expanded, now, WINDOW_MIN, {
    send: async () => {},
    scheduleAll: async (f) => { fires = f; },
  });

  const firesEntry = (id: string) =>
    fires.some((f) => f.payload.taskIds?.some((tid) => tid === id || tid.startsWith(`${id}::v::`)));
  const remDesc = (offs: number[]) =>
    offs.length === 0 ? "none" : offs.map((o) => (o === 0 ? "at due time" : `${o}m before`)).join(", ");

  const lines: string[] = [];
  lines.push(`Now: ${now.toLocaleString()}`);
  lines.push(`Tasks/events stored: ${live.length}`);
  lines.push(`  events: ${events.length}   tasks: ${live.length - events.length}`);
  lines.push(`  with a reminder set: ${withReminder.length}`);

  // The entries you just created/edited (Google-synced ones filtered out), so a
  // "I changed this and nothing happened" is explained in concrete terms.
  const recent = live
    .filter((t) => !t.external_id)
    .sort((a, b) => (b.updated_at ?? "").localeCompare(a.updated_at ?? ""))
    .slice(0, 3);
  if (recent.length > 0) {
    lines.push("");
    lines.push("Recently edited by you:");
    for (const t of recent) {
      lines.push(`  • "${t.title || "(untitled)"}" — ${t.is_event ? "EVENT" : "task"}`);
      lines.push(`      due ${t.due_at ? new Date(t.due_at).toLocaleString() : "none"} · reminder ${remDesc(t.reminder_offsets)}`);
      lines.push(`      will fire a reminder: ${firesEntry(t.id) ? "YES" : "NO"}`);
    }
  }

  lines.push("");
  lines.push(`Reminders the app wants to fire (next ${WINDOW_MIN / 60 / 24}d): ${fires.length}`);
  for (const f of fires.slice(0, 6)) {
    lines.push(`  • ${new Date(f.atMs).toLocaleString()} — ${f.payload.title}`);
  }
  if (fires.length > 6) lines.push(`  …and ${fires.length - 6} more`);
  // Android only hands the OS the soonest MAX_SCHEDULED alarms; warn when the
  // wanted set exceeds that so a dropped far-future reminder isn't a mystery.
  if (detectPlatform() === "capacitor" && fires.length > MAX_SCHEDULED) {
    lines.push(
      `  ⚠ Only the soonest ${MAX_SCHEDULED} are scheduled on Android — ${fires.length - MAX_SCHEDULED} further-out reminder(s) wait until others fire.`,
    );
  }

  if (detectPlatform() === "capacitor") {
    lines.push("");
    try {
      const d = await capacitorDiagnostics();
      lines.push(`Permission: ${d.permission}`);
      if (d.permission !== "granted") {
        lines.push("  ⚠ Notifications aren't allowed — reminders won't fire in the background.");
        lines.push("    Grant them in Android Settings → Apps → YourCalendar → Notifications.");
      }
      lines.push(`Channel importance: ${d.channelImportance ?? "n/a"} (need ≥3)`);
      lines.push(`Actually scheduled with the OS: ${d.pending.length}`);
      for (const p of d.pending.slice(0, 6)) lines.push(`  • ${p.at ?? "?"} — ${p.title}`);
    } catch (e) {
      lines.push(`OS query failed: ${e instanceof Error ? e.message : String(e)}`);
    }
  }
  return lines.join("\n");
}
